import {
  effect,
  Injectable,
  makeStateKey,
  signal,
  StateKey,
  TransferState,
} from '@angular/core';
import { ApiResponse, HttpService } from './http.service';
import { NotificationService } from './notification.service';
import { SportFilterService } from './sport-filter.service';
import { SportType } from '../../../../shared/constants/sport-type';
import { config } from '../../../../shared/config';
import { ITournament } from '../../../../server/interfaces/tournament';
import { ILocationCoordinate } from '../../../../shared/interfaces/location';
import { ITournamentDraw } from '../../../../server/interfaces/tournament-draw';
import { ITournamentGroupStage } from '../../../../server/interfaces/tournament-group-stage';
import { IPublicHostInfo } from '../../../../shared/interfaces/tourney-rank';
import { calculateDistance } from '../helpers/distance';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class TournamentService {
  // Signals
  filter = signal<string>('');
  lastFilter = signal<string>('');
  nearbyTournaments = signal<ITournament[]>([]);
  gettingNearbyTournaments = signal<boolean>(false);
  endOfNearbyTournaments = signal<boolean>(false);
  cursor = signal<number>(Date.now());

  tournament = signal<ITournament | null>(null);
  gettingTournament = signal<boolean>(false);
  tournamentTeams = signal<any[]>([]);

  tournamentGroupStages = signal<ITournamentGroupStage[]>([]);
  tournamentDraw = signal<ITournamentDraw | null>(null);

  publicHostInfo = signal<IPublicHostInfo | null>(null);

  initialized = signal(false);

  constructor(
    private http: HttpService,
    private notification: NotificationService,
    private sportFilterService: SportFilterService,
    private transferState: TransferState,
    private platform: PlatformService
  ) {}

  /**
   * Fetches nearby tournaments based on coordinates and sport filter
   * @param sportType Sport type to filter by
   * @param coordinates User's location coordinates
   * @param page Page number for pagination
   * @param resetCursor Whether to reset the cursor for pagination
   */
  async getNearbyTournaments(
    sportType?: SportType | '',
    coordinates?: ILocationCoordinate,
    page?: number,
    resetCursor = false
  ): Promise<boolean> {
    this.initialized.set(true);
    this.nearbyTournaments.set([]);
    this.endOfNearbyTournaments.set(false);

    const stateKey: StateKey<number> = makeStateKey<number>(
      'transfer-get-nearby-tournaments-cursor'
    );

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<number>(stateKey, {} as number);
      this.transferState.remove(stateKey);
      this.cursor.set(data);
    } else if (resetCursor) this.cursor.set(Date.now());

    // Get nearby tournaments
    const offset = (page ? page - 1 : 0) * config.dataTake;

    this.gettingNearbyTournaments.set(true);
    const res = await this.http.get<ITournament[]>(
      `/tournament/nearby-by-coordinates`,
      {
        lat: coordinates?.coordinates[1] ?? '',
        long: coordinates?.coordinates[0] ?? '',
        sportType: this.sportFilterService.filter(),
        offset,
        filter: this.filter(),
        cursor: this.cursor(),
      }
    );
    if (this.platform.isServer()) {
      this.transferState.set(stateKey, this.cursor());
    }
    this.gettingNearbyTournaments.set(false);

    if (res.error) {
      this.notification.error(res.error);
      return false;
    }

    if (res.data) {
      this.nearbyTournaments.set(res.data);
      this.endOfNearbyTournaments.set(res.data.length < config.dataTake);
    }

    return true;
  }

  /**
   * Gets a specific tournament by ID with location coordinates
   * @param id Tournament ID
   * @param coordinates User's location coordinates
   */
  async getTournament(
    id: string,
    coordinates?: ILocationCoordinate
  ): Promise<void> {
    this.gettingTournament.set(true);

    let res;

    const stateKey: StateKey<ApiResponse<ITournament>> = makeStateKey<
      ApiResponse<ITournament>
    >(`transfer-get-tournament-${id}`);

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<ITournament>>(
        stateKey,
        {} as ApiResponse<ITournament>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<ITournament>(
        `/tournament/get-with-coordinates/${id}`,
        {
          lat: coordinates?.coordinates[1] ?? '',
          long: coordinates?.coordinates[0] ?? '',
        }
      );
    }

    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }

    this.gettingTournament.set(false);

    if (res.data) {
      this.tournament.set(res.data);
    } else {
      this.tournament.set(null);
      this.notification.error(res.error);
    }
  }

  /**
   * Gets teams for a specific tournament
   * @param tournamentId Tournament ID
   */
  async getTournamentTeams(tournamentId: string): Promise<void> {
    if (!tournamentId) return;

    try {
      const res = await this.http.get<any[]>(
        `/tournament-team/${tournamentId}`,
        null
      );

      if (res.data) {
        this.tournamentTeams.set(res.data);
      } else {
        this.tournamentTeams.set([]);
        this.notification.error(res.error);
      }
    } catch (error) {
      this.notification.error('Failed to load tournament teams');
      console.error('Error loading tournament teams', error);
    }
  }

  /**
   * Gets the thumbnail for a tournament participant
   * @param gender Participant gender
   * @param thumbnail Optional custom thumbnail URL
   */

  /**
   * Set filter for tournament search
   * @param searchTerm Search term to filter tournaments
   */
  setFilter(searchTerm: string): void {
    this.filter.set(searchTerm);
  }

  /**
   * Resets the search filter
   */
  resetFilter(): void {
    this.filter.set('');
  }

  async getTournamentDrawByTournamentId(tournamentId: string) {
    this.tournamentDraw.set(null);
    const res = await this.http.get<ITournamentDraw>(
      `/tournament-draw/get-by-tournament-id/${tournamentId}`,
      null
    );
    if (res.data) {
      this.tournamentDraw.set(res.data);
    } else {
      this.tournamentDraw.set(null);
      this.notification.error(res.error);
    }
  }

  async getTournamentDrawById(drawId: string) {
    this.tournamentDraw.set(null);
    const res = await this.http.get<ITournamentDraw>(
      `/tournament-draw/get-by-id/${drawId}`,
      null
    );
    if (res.data) {
      this.tournamentDraw.set(res.data);
    } else {
      this.tournamentDraw.set(null);
      this.notification.error(res.error);
    }
  }

  async getTournamentGroupStages(tournamentId: string) {
    this.tournamentGroupStages.set([]);
    const res = await this.http.get<ITournamentGroupStage[]>(
      `/tournament-group-stage/${tournamentId}`,
      null
    );
    if (res.data) {
      this.tournamentGroupStages.set(res.data);
    } else {
      this.tournamentGroupStages.set([]);
      this.notification.error(res.error);
    }
  }

  /**
   * Fetches the public host-verification info (Verified Host card data) for
   * an official tournament's first organizer. No-auth endpoint — safe to
   * call during SSR. Uses the same TransferState hydration pattern as
   * getTournament so the server-rendered fetch isn't repeated on the client.
   */
  async getPublicHostInfo(organizerId: string): Promise<IPublicHostInfo | null> {
    let res;

    const stateKey: StateKey<ApiResponse<IPublicHostInfo>> = makeStateKey<
      ApiResponse<IPublicHostInfo>
    >(`transfer-get-public-host-info-${organizerId}`);

    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<IPublicHostInfo>>(
        stateKey,
        {} as ApiResponse<IPublicHostInfo>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<IPublicHostInfo>(
        `/host-verification/public/${organizerId}`,
        null
      );
    }

    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }

    if (res.error || !res.data) {
      this.publicHostInfo.set(null);
      return null;
    }

    this.publicHostInfo.set(res.data);
    return res.data;
  }

  async getTournamentGroupStage(
    tournamentId: string,
    groupStageId: string
  ): Promise<ITournamentGroupStage | null> {
    const res = await this.http.get<ITournamentGroupStage>(
      `/tournament-group-stage/${tournamentId}/${groupStageId}`,
      null
    );
    if (res.data) {
      return res.data;
    } else {
      this.notification.error(res.error);
      return null;
    }
  }
}
